// The competition + season selects every event module (Heat Map, Shot Map, Pass Map,
// Passing Profile) adds to main.js's existing player strip — ported from
// renderers-shotmap.js ensureEventSubControls / renderEventSubControls (and Heat Map's /
// Passing Profile's own copies of the same two wraps). needsPlayer:true, so it appends
// INTO the strip via <SubControlsSlot>.
//
// With no event data the legacy keeps both wraps but empties them — so do we (the strip's
// layout doesn't jump when switching to a player without data). `children` = anything a
// module adds after the two selects (Shot/Pass Map's Chart/Table toggle).

import { useMemo, type ReactNode } from "react";
import type { ProtoShotPassDetail } from "@catbento/shared";
import { SubControlsSlot } from "./SubControlsSlot.js";
import { StyledSelect, type StyledSelectItem } from "./StyledSelect.js";
import { humanizeSeasonCode, type ResolvedEventState } from "../engines/event-map-kit.js";

interface Props {
  mod: HTMLElement;
  spd: ProtoShotPassDetail | null;
  state: ResolvedEventState | null;
  onCompetitionChange: (compId: string) => void;
  onSeasonChange: (seasonCode: string) => void;
  children?: ReactNode;
}

export function EventSubControls({ mod, spd, state, onCompetitionChange, onSeasonChange, children }: Props) {
  // Memoised so StyledSelect's build effect doesn't rebuild the legacy dropdown every render.
  const compItems = useMemo<StyledSelectItem[]>(
    () => (spd ? spd.competitions.map((c) => ({ value: String(c.id), label: c.name })) : []),
    [spd],
  );
  const seasonItems = useMemo<StyledSelectItem[]>(
    () => (state ? state.seasonCodes.map((s) => ({ value: s, label: humanizeSeasonCode(s) })) : []),
    [state],
  );

  return (
    <SubControlsSlot mod={mod}>
      {spd && state ? (
        <>
          <StyledSelect
            items={compItems}
            value={state.compId}
            onChange={onCompetitionChange}
            wrapClass="event-competition-select-wrap"
            extraClass="event-competition-select"
          />
          <StyledSelect
            items={seasonItems}
            value={state.seasonCode}
            onChange={onSeasonChange}
            wrapClass="event-season-select-wrap"
            extraClass="event-season-select"
          />
        </>
      ) : (
        <>
          <div className="event-competition-select-wrap" />
          <div className="event-season-select-wrap" />
        </>
      )}
      {children}
    </SubControlsSlot>
  );
}
